// Tileset URL override + tile-format detection (framework-agnostic).
//
// Ported from the 1.0 viewer's apiCall.updateTileUrl. Given a style and a
// custom tileset URL, rewrites the vector sources to point at that URL so the
// viewer can preview an arbitrary tileset against a known stylesheet.
//
// Format detection is split out as a pure function (detectTileFormat) so it is
// unit-testable without network; the URL rewrite (applyTilesetUrl) performs the
// same TileJSON resolution the 1.0 viewer did.

import type { StyleJson } from "../types";

/** Tile formats the viewer recognizes. P0 = MVT; P1 = PMTiles; P2 = MLT. */
export type TileFormat = "mvt" | "pmtiles" | "mlt" | "unknown";

/** Sources whose `tiles` array gets repointed to the override URL. */
const OVERRIDABLE_SOURCES = ["composite", "AmznVector"];

/**
 * Detects the tile format implied by a tileset URL.
 *
 * - `pmtiles://...` or a `.pmtiles` archive  → "pmtiles"
 * - a `.mlt` tile template                   → "mlt"
 * - `.mvt` / `.pbf` / `{z}/{x}/{y}` template → "mvt"
 * - anything else                            → "unknown"
 *
 * Pure and synchronous; case-insensitive; query strings are ignored.
 */
export function detectTileFormat(tilesetUrl: string): TileFormat {
    if (!tilesetUrl) {
        return "unknown";
    }
    const url = tilesetUrl.trim().toLowerCase();
    const path = url.split("?")[0];

    if (url.startsWith("pmtiles://") || path.endsWith(".pmtiles")) {
        return "pmtiles";
    }
    if (path.endsWith(".mlt") || path.includes("{z}/{x}/{y}.mlt")) {
        return "mlt";
    }
    if (
        path.endsWith(".mvt") ||
        path.endsWith(".pbf") ||
        path.includes("{z}/{x}/{y}")
    ) {
        return "mvt";
    }
    return "unknown";
}

/** Fetches and parses TileJSON from a URL. Injectable for testing. */
export type TileJsonFetcher = (url: string) => Promise<any>;

const defaultFetcher: TileJsonFetcher = async (url: string) => {
    const response = await fetch(url);
    return response.json();
};

/**
 * Returns a new style with vector sources rewritten to use `tilesetUrl`.
 *
 * Mirrors the 1.0 viewer's updateTileUrl: any vector source with a TileJSON
 * `url` is resolved inline (the `url` replaced by the fetched TileJSON), then
 * the `tiles` array of `composite`/`AmznVector` sources is repointed to the
 * override URL. The input style is not mutated.
 *
 * @param tilesetUrl  Override tile URL (already trimmed by the caller).
 * @param styleJson   Source style.
 * @param fetcher     TileJSON fetcher (defaults to global fetch); injectable.
 */
export async function applyTilesetUrl(
    tilesetUrl: string,
    styleJson: StyleJson,
    fetcher: TileJsonFetcher = defaultFetcher
): Promise<StyleJson> {
    const styleBody: StyleJson = JSON.parse(JSON.stringify(styleJson));

    for (const sourceId of Object.keys(styleBody.sources ?? {})) {
        const source = styleBody.sources[sourceId];

        // Resolve a TileJSON `url` into an inline vector source.
        if (source.url !== undefined && source.type === "vector") {
            const tileBody = await fetcher(source.url);
            delete source.url;
            styleBody.sources[sourceId] = { type: "vector", ...tileBody };
        }

        // Repoint the tiles array for the known overridable sources.
        if (tilesetUrl && OVERRIDABLE_SOURCES.indexOf(sourceId) !== -1) {
            styleBody.sources[sourceId].tiles = [tilesetUrl];
        }
    }

    return styleBody;
}
